import {
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { MouseEvent, useRef, useState } from 'react';

import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useAuthentication } from '../authentication/context/useAuthentication';
import useUser from '../authentication/context/useUser';
import PasswordChangeModal from '../authentication/PasswordChangeModal';
import ProfileChangeModal from '../authentication/ProfileChangeModal';
import { ReactComponent as AccountIcon } from '../ui/icons/account.svg';
import { ReactComponent as PasswordIcon } from '../ui/icons/change-password.svg';
import { ReactComponent as ExitIcon } from '../ui/icons/exit.svg';

// https://stackoverflow.com/a/72595895/1665540
const contrastColor = (backgroundColor: string) => {
  try {
    return ['#060606', 'white'][
      ~~(
        [1, 3, 5]
          .map((p) => parseInt(backgroundColor.substr(p, 2), 16))
          .reduce((r, v, i) => [0.299, 0.587, 0.114][i] * v + r, 0) < 128
      )
    ];
  } catch (e) {
    return '#060606';
  }
};

const stringToColor = (name: string, saturation = 100, lightness = 75) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  return {
    bgcolor: `hsl(${hash % 360}, ${saturation}%, ${lightness}%)`,
    color: contrastColor(`${hash % 360}`),
  };
};

const stringAvatar = (name: string) => {
  return {
    sx: {
      cursor: 'pointer',
      border: '2px solid #060606',
      ...stringToColor(name),
    },
    children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
  };
};

const Title = styled.p`
  font-size: 1rem;
  font-weight: 500;
  padding: 16px;
  max-width: 250px;
  text-align: center;

  span {
    font-weight: bold;
  }
`;

const UserMenu = () => {
  const { t } = useTranslation(['authentication', 'common']);
  const { logout } = useAuthentication();
  const { user } = useUser();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePasswordChangeClick = () => {
    handleClose();
    setPasswordModalOpen(true);
  };

  const handleProfileChangeClick = () => {
    handleClose();
    setProfileModalOpen(true);
  };

  return (
    <>
      <PasswordChangeModal
        open={passwordModalOpen}
        handleClose={() => setPasswordModalOpen(false)}
      ></PasswordChangeModal>
      {profileModalOpen && <ProfileChangeModal
        open={profileModalOpen}
        handleClose={() => setProfileModalOpen(false)}
      />}
      <Avatar
        {...stringAvatar(`${user?.firstName[0]} ${user?.lastName[0]}`)}
        onClick={handleClick}
        ref={avatarRef}
        sx={{ backgroundColor: 'var(--TEAROSE)', color: "black", cursor: 'pointer' }}
      />

      <Menu
        id="user-menu"
        open={open}
        anchorEl={avatarRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Title>
          {t('connected-as')}{' '}
          <span>
            {user?.firstName} {user?.lastName}
          </span>
        </Title>
        <Divider sx={{ margin: '8px 0' }} />
        <MenuItem onClick={handleProfileChangeClick}>
          <ListItemIcon>
            <AccountIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('edit-profile')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePasswordChangeClick}>
          <ListItemIcon>
            <PasswordIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('change-password')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={logout}>
          <ListItemIcon>
            <ExitIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
